import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../api/client'

interface MailAntwort {
  host: string | null
  port: number | null
  benutzer: string | null
  passwortHinterlegt: boolean
  starttls: boolean
  ssl: boolean
  absenderAdresse: string | null
  absenderName: string | null
  versandAktiv: boolean
}

interface Ergebnis {
  erfolgreich: boolean
  meldung: string
}

export function MailEinstellungen() {
  const [daten, setDaten] = useState<MailAntwort | null>(null)
  const [passwort, setPasswort] = useState('')
  const [testEmpfaenger, setTestEmpfaenger] = useState('')
  const [meldung, setMeldung] = useState<{ art: string; text: string } | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  async function laden() {
    try {
      setDaten(await api.get<MailAntwort>('/api/plattform/mail'))
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Laden fehlgeschlagen' })
    }
  }

  useEffect(() => {
    void laden()
  }, [])

  async function speichern(ereignis: FormEvent) {
    ereignis.preventDefault()
    if (!daten) return
    setLaeuft(true)
    setMeldung(null)
    try {
      // Ein leeres Passwortfeld bedeutet „unverändert" — die Oberfläche zeigt das
      // gespeicherte Passwort nie an.
      const antwort = await api.put<MailAntwort>('/api/plattform/mail', {
        ...daten,
        passwort: passwort || null,
      })
      setDaten(antwort)
      setPasswort('')
      setMeldung({ art: 'erfolg', text: 'Einstellungen gespeichert' })
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  async function verbindungPruefen() {
    setLaeuft(true)
    try {
      const e = await api.post<Ergebnis>('/api/plattform/mail/pruefen')
      setMeldung({ art: e.erfolgreich ? 'erfolg' : 'fehler', text: e.meldung })
    } finally {
      setLaeuft(false)
    }
  }

  async function testmail() {
    if (!testEmpfaenger) return
    setLaeuft(true)
    try {
      const e = await api.post<Ergebnis>('/api/plattform/mail/test', { empfaenger: testEmpfaenger })
      setMeldung({ art: e.erfolgreich ? 'erfolg' : 'hinweis', text: e.meldung })
    } catch (ex) {
      setMeldung({ art: 'fehler', text: ex instanceof Error ? ex.message : 'Versand fehlgeschlagen' })
    } finally {
      setLaeuft(false)
    }
  }

  if (!daten) return <div className="laden">Wird geladen …</div>

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <h1>Mail-Einstellungen</h1>
          <p>SMTP-Zugang für Einladungen, Passwort-Zurücksetzung und Berichte.</p>
        </div>
      </div>

      {meldung && <div className={`meldung ${meldung.art}`}>{meldung.text}</div>}

      {!daten.versandAktiv && (
        <div className="meldung hinweis">
          Der Versand ist abgeschaltet. Einladungen werden erzeugt, aber nicht verschickt — der
          Link wird stattdessen beim Anlegen angezeigt, damit Sie ihn weitergeben können.
        </div>
      )}

      <div className="karte abschnitt">
        <form onSubmit={speichern}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
            <div className="feld">
              <label htmlFor="host">SMTP-Server</label>
              <input
                id="host"
                type="text"
                placeholder="mail.example.de"
                value={daten.host ?? ''}
                onChange={(e) => setDaten({ ...daten, host: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="port">Port</label>
              <input
                id="port"
                type="number"
                placeholder="587"
                value={daten.port ?? ''}
                onChange={(e) =>
                  setDaten({ ...daten, port: e.target.value ? Number(e.target.value) : null })
                }
              />
            </div>
            <div className="feld">
              <label htmlFor="benutzer">Benutzername</label>
              <input
                id="benutzer"
                type="text"
                value={daten.benutzer ?? ''}
                onChange={(e) => setDaten({ ...daten, benutzer: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="passwort">
                Passwort {daten.passwortHinterlegt && '(hinterlegt — leer lassen = unverändert)'}
              </label>
              <input
                id="passwort"
                type="password"
                placeholder={daten.passwortHinterlegt ? '••••••••' : ''}
                value={passwort}
                onChange={(e) => setPasswort(e.target.value)}
              />
            </div>
            <div className="feld">
              <label htmlFor="absender">Absenderadresse</label>
              <input
                id="absender"
                type="email"
                placeholder="noreply@example.de"
                value={daten.absenderAdresse ?? ''}
                onChange={(e) => setDaten({ ...daten, absenderAdresse: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="absenderName">Absendername</label>
              <input
                id="absenderName"
                type="text"
                placeholder="VLZHUB"
                value={daten.absenderName ?? ''}
                onChange={(e) => setDaten({ ...daten, absenderName: e.target.value })}
              />
            </div>
            <div className="feld">
              <label htmlFor="verschluesselung">Verschlüsselung</label>
              <select
                id="verschluesselung"
                value={daten.ssl ? 'ssl' : daten.starttls ? 'starttls' : 'keine'}
                onChange={(e) =>
                  setDaten({
                    ...daten,
                    ssl: e.target.value === 'ssl',
                    starttls: e.target.value === 'starttls',
                  })
                }
              >
                <option value="starttls">STARTTLS (meist Port 587)</option>
                <option value="ssl">SSL/TLS (meist Port 465)</option>
                <option value="keine">keine</option>
              </select>
            </div>
            <div className="feld">
              <label htmlFor="versand">Versand</label>
              <select
                id="versand"
                value={daten.versandAktiv ? 'ja' : 'nein'}
                onChange={(e) => setDaten({ ...daten, versandAktiv: e.target.value === 'ja' })}
              >
                <option value="ja">aktiv — Mails werden verschickt</option>
                <option value="nein">abgeschaltet — Mails nur protokollieren</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="submit" className="knopf-primaer" disabled={laeuft}>
              Speichern
            </button>
            <button
              type="button"
              className="knopf-schlicht"
              disabled={laeuft}
              onClick={() => void verbindungPruefen()}
            >
              Verbindung prüfen
            </button>
          </div>
        </form>
      </div>

      <div className="karte">
        <h2>Testmail</h2>
        <p style={{ color: 'var(--grau-500)', fontSize: '0.86rem', marginTop: 6 }}>
          Verschickt eine Einladungsvorlage mit Beispieldaten an die angegebene Adresse.
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="feld" style={{ marginBottom: 0, minWidth: 260 }}>
            <label htmlFor="test">Empfänger</label>
            <input
              id="test"
              type="email"
              value={testEmpfaenger}
              onChange={(e) => setTestEmpfaenger(e.target.value)}
            />
          </div>
          <button
            className="knopf-akzent"
            disabled={laeuft || !testEmpfaenger}
            onClick={() => void testmail()}
          >
            Testmail senden
          </button>
        </div>
      </div>
    </>
  )
}
